import { Button, useTranslation } from '@hbs-us/ui';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { apiFetchJson } from '../../lib/apiFetch.js';

/**
 * Self-serve "Delete my account" (Surface D — D-16 / SEC-12). A destructive entry that opens a
 * focus-trapped confirm dialog: the recoverable-30-days consequence copy, a single `destructive`
 * confirm (NO type-to-confirm — recoverable), and a default-focused, Escape-bound "Go back" /
 * "Volver" safe-exit. On confirm it POSTs `/api/v1/account/delete` and surfaces the
 * "Account scheduled for erasure" copy. EN+ES, token-driven (red destructive), zero hardcoded hex.
 *
 * The dashboard has no Radix; this is a small self-contained accessible AlertDialog — focus moves to
 * the safe-exit on open, Escape = the safe-exit, focus returns to the trigger on close, a backdrop
 * + a focus trap keep the interaction modal (A11Y-01 destructive-dialog obligations).
 */

type DeleteState = 'idle' | 'submitting' | 'done' | 'error';

export function DeleteMyAccountEntry() {
  const { t } = useTranslation('notifications');
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<DeleteState>('idle');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const goBackRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const bodyId = useId();

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  // Default-focus the "Go back" safe-exit on open; Escape closes to it; trap focus inside.
  useEffect(() => {
    if (!open) return;
    goBackRef.current?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href]',
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, close]);

  const handleConfirm = async () => {
    setState('submitting');
    try {
      await apiFetchJson<{ ok: boolean }>('/api/v1/account/delete', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      setState('done');
    } catch {
      setState('error');
    }
  };

  return (
    <section className="mt-4 flex flex-col gap-3 rounded-md border border-destructive/40 bg-card p-4">
      <h2 className="font-semibold text-destructive text-sm">{t('erasure.dangerZone')}</h2>

      <Button
        ref={triggerRef}
        variant="destructive"
        className="w-fit"
        onClick={() => {
          setState('idle');
          setOpen(true);
        }}
      >
        {t('erasure.entry')}
      </Button>

      {open ? (
        <>
          <div aria-hidden="true" className="fixed inset-0 z-40 bg-background/80" />
          <div
            ref={dialogRef}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={bodyId}
            className="-translate-x-1/2 -translate-y-1/2 fixed top-1/2 left-1/2 z-50 w-[min(28rem,92vw)] rounded-md border border-border bg-card p-6 shadow-lg"
          >
            <h3 id={titleId} className="font-display font-semibold text-foreground text-xl">
              {t('erasure.confirmTitle')}
            </h3>
            <p id={bodyId} className="mt-2 text-muted-foreground text-sm">
              {state === 'done' ? t('erasure.scheduled') : t('erasure.confirmBody')}
            </p>

            {state === 'error' ? (
              <p role="alert" className="mt-3 text-destructive text-sm">
                {t('erasure.error')}
              </p>
            ) : null}

            <div className="mt-6 flex items-center justify-end gap-2">
              <Button ref={goBackRef} variant="secondary" onClick={close}>
                {t('erasure.goBack')}
              </Button>
              {state !== 'done' ? (
                <Button
                  variant="destructive"
                  disabled={state === 'submitting'}
                  onClick={() => void handleConfirm()}
                >
                  {t('erasure.confirmCta')}
                </Button>
              ) : null}
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}
